import { Component, OnInit } from '@angular/core';
import { ModalController, ToastController } from '@ionic/angular';
import { ProfileViewerComponent } from '../profile-viewer/profile-viewer.component';
import { environment } from 'environment';
import { BehaviorSubject } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { AuthenticatorService } from '@aws-amplify/ui-angular';
import { Memory } from 'src/classes/Memories';
import { GlobalService } from '../services/global.service';
import { AuthSession, fetchAuthSession } from 'aws-amplify/auth';
import { AudioComment, AudioCommentsResponse } from 'infrastructure/lib/src/models/audio-comment';
import BaseLevelScene from '../Scenes/BaseLevelScene';
import MainScene from '../Scenes/MainScene';
import { UserAudio } from 'infrastructure/lib/src/models/user-audio';

@Component({
  selector: 'app-audio-modal',
  templateUrl: './audio-modal.component.html',
  styleUrls: ['./audio-modal.component.scss'],
})
export class AudioModalComponent{
  audioMetadata: UserAudio;
  username: string;
  displayProfileButton: boolean;
  scene: MainScene;
  session: AuthSession;
  public audioSrc: string;
  lastEvaluatedKey: any = null;
  isLoading: boolean = false;

  // display the number of likes the audio has
  likes$ = new BehaviorSubject<number>(0);
  myComment$ = new BehaviorSubject<string>("");
  allComments$ = new BehaviorSubject<Array<AudioComment>>([]);
  description = "";
  previousDescription = "";
  descriptionEnabled= false;
  protected GlobalService = GlobalService;

  constructor(private modalCtrl: ModalController, private http: HttpClient, private toastController: ToastController, public globalService: GlobalService, public authenticator: AuthenticatorService) {}

  async ngOnInit() {
    this.session = await fetchAuthSession();

    this.isLoading = false;

    // get the audio source signed URL from s3
    const s3AudioSignedURL = await fetch(`${environment.REST_API}get-signed-url?bucket=st-audio-bucket&key=${this.audioMetadata.s3Path}`);

    // extract the username from the memory name
    this.username = this.audioMetadata.s3Path.split('/')[0];

    this.audioSrc = (await s3AudioSignedURL.json()).signedUrl!.toString();

    // Play the audio automatically when modal opens.
    this.playAudio();

    // retrieve the current number of likes this memory has
    this.http.get(`${environment.REST_API}/metadataAudio?s3Path=${this.audioMetadata.s3Path}`).subscribe({
      next: data => {
          this.description = data["description"];
          this.likes$.next(data["likes"]);
      },
      error: error => {
          console.error('There was an error retrieving the audio likes!', error);
      }
    });

    // retrieve the comments for this audio.
    this.loadInitialComments();
  }

  playAudio(){
    try {
      const audio = new Audio(this.audioSrc);
      audio.crossOrigin = 'anonymous';
      audio.play().then(() => {
        console.log('Audio playing');
        if(!MainScene.music.isPaused) {
          MainScene.music.pause();
        }
      }).catch((err) => {
        console.error('Error playing audio', err);
        if(MainScene.music.isPaused) {
          MainScene.music.play();
        }
      });
      audio.addEventListener('ended', () => {
        if(MainScene.music.isPaused) {
          MainScene.music.play();
        }
      });
      audio.addEventListener('error', (e) => {
        console.error('Audio playback error', e);
        if(MainScene.music.isPaused) {
          MainScene.music.play();
        }
      });
    } catch (err) {
      console.error('Failed to create/play audio', err);
      if(MainScene.music.isPaused) {
        MainScene.music.play();
      }
    }
  }

  // Close the modal
  cancel() {
    console.log("audio-modal cancel clicked");
    if(this.scene){
      const activeScenes = this.scene.scene.manager.getScenes(true) as Array<BaseLevelScene>;
      activeScenes.forEach((scene) => {
        scene.scene.resume();
      });
    }
    return this.modalCtrl.dismiss(null, 'cancel');
  }

  // Handle infinite scroll
  async loadMore(event: any) {
    if (this.isLoading || !this.lastEvaluatedKey) {
      event.target.complete();
      if (!this.lastEvaluatedKey) {
        event.target.disabled = true;
      }
      return;
    }

    try {
      const result = await this.getAudioComments();
      event.target.complete();

      // Disable infinite scroll if no more data
      if (!result.hasMore) {
        event.target.disabled = true;
      }
    } catch (error) {
      console.error('Error loading more comments:', error);
      event.target.complete();
    }
  }

  // Initial load
  async loadInitialComments() {
    try {
      // Reset pagination
      this.lastEvaluatedKey = null;
      this.allComments$.next([]);

      await this.getAudioComments(true);
    } catch (error) {
      console.error('Error loading initial comments:', error);
    }
  }

  convertTimestampToDateTime(timestamp: number) {
    // Create a Date object from the timestamp (in milliseconds)
    const date = new Date(timestamp);
  
    // Extract date components
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // JavaScript months are 0-indexed
    const day = date.getDate();
  
    // Extract time components
    const hours = date.getHours();
    const minutes = date.getMinutes();
  
    // Format the date and time string
    const formattedDateTime = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')} ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  
    return formattedDateTime;
  }
  
  
  async openProfileModal() {
    await this.modalCtrl.dismiss();
    this.modalCtrl.create({
      component: ProfileViewerComponent,
      componentProps: { 
        username: this.username,
        scene: this.scene
      }
    }).then((modal) => {
      console.log("Presenting the profile modal");
      modal.present();
      GlobalService.incrementModalCount();
      modal.onDidDismiss().then(() => {
        GlobalService.decrementModalCount();
      });
    });
  }

  async likeAudio(){
    console.log("filePath: ", this.audioMetadata.s3Path);
    const postBody = JSON.stringify({s3Path: this.audioMetadata.s3Path});
    // Add to end of post request
    this.http.post(`${environment.REST_API}/like`, postBody).subscribe({
      next: async data => {
          this.likes$.next(data["likes"]);

          const toast = await this.toastController.create({
            message: 'Mix Tape liked!',
            duration: 1500,
            color: 'success',
            position: 'top',
          });
          await toast.present();
      },
      error: async error => {
          console.error('There was an error!', error);
          const toast = await this.toastController.create({
            message: 'Error liking this Mix Tape!',
            duration: 1500,
            color: 'warning',
            position: 'top',
          });
          await toast.present();
      }
    });
  }

  editDescription(){
    this.descriptionEnabled = true;
    this.previousDescription = this.description;
  }

  async updateDescription(){
    console.log("filePath: ", this.audioMetadata.s3Path);
    const postBody = JSON.stringify({s3Path: this.audioMetadata.s3Path, description: this.description});
    // Add to end of post request
    this.http.patch(`${environment.REST_API}/audio/description`,
      postBody,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.session.tokens.idToken}`
        }),
        }
    ).subscribe({
      next: async data => {
          console.log('data:', data);
          this.description = data["description"];
          this.descriptionEnabled = false;
          const toast = await this.toastController.create({
            message: 'Audio description updated!',
            duration: 1500,
            color: 'success',
            position: 'top',
          });
          await toast.present();
      },
      error: async error => {
          console.error('There was an error!', error);
          const toast = await this.toastController.create({
            message: 'Error updating audio description!',
            duration: 1500,
            color: 'warning',
            position: 'top',
          });
          await toast.present();
      }
    });
  }

  cancelDescription(){
    this.descriptionEnabled = false;
    this.description = this.previousDescription
  }

  async viewProfile(username: string){
    this.modalCtrl.create({
      component: ProfileViewerComponent,
      componentProps: { 
        username: username,
        scene: null
      }
    }).then((modal) => {
      console.log("Presenting the profile modal");
      modal.present();
      GlobalService.incrementModalCount();
      modal.onDidDismiss().then(() => {
        GlobalService.decrementModalCount();
      });
    });

  }

  goToAudioLocation(){
    const { x, y } = Memory.extractCoordsFromFilename(this.audioMetadata.s3Path);
    GlobalService.$playerXY.next({x: x, y: y+100});
  }

  trackNewCommentText(commentText: string){
    this.myComment$.next(commentText);
  }

  submitComment(){
    // submit audio comment to api endpoint
    const postBody = JSON.stringify({s3Path: this.audioMetadata.s3Path, comment: this.myComment$.value});
    this.http.post(`${environment.REST_API}/commentAudio`, postBody,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.session.tokens.idToken}`
        }),
        }
    ).subscribe({
      next: async data => {
          console.log("Submitted post!");
          console.log(data);
          this.myComment$.next("");

          // Add the new comment to the list of comments.
          this.allComments$.next([
            ...this.allComments$.getValue(),
            data["audioComment"]
          ].sort((a, b) => b.timestamp - a.timestamp));

          const toast = await this.toastController.create({
            message: 'Comment successfully added!',
            duration: 1500,
            color: 'success',
            position: 'top',
          });
          await toast.present();
      },
      error: async error => {
          console.error('There was an error submitting your comment!', error);

          const toast = await this.toastController.create({
            message: 'There was an error submitting your comment!',
            duration: 1500,
            color: 'warning',
            position: 'top',
          });
          await toast.present();
      }
    });
  }

  async getAudioComments(isInitialLoad = false): Promise<{hasMore: boolean}> {
    try {
      // Build query parameters
      let params = new HttpParams()
        .set('s3Path', this.audioMetadata.s3Path);

      if (!isInitialLoad && this.lastEvaluatedKey) {
        params = params.set('lastEvaluatedKey', 
          encodeURIComponent(JSON.stringify(this.lastEvaluatedKey)));
      }

      this.isLoading = true;

      // Convert the subscription to a Promise.
      return new Promise((resolve, reject) => {
        this.http.get(`${environment.REST_API}/commentAudio`, {params})
          .subscribe({
            next: (data: AudioCommentsResponse) => {
              console.log("Retrieved all audio comments!");
              console.log("data:");
              console.log(data);

              // Defensive: ensure audioComments is an array
              const audioComments = Array.isArray(data?.audioComments) ? data.audioComments : [];

              console.log("lastEvaluatedKey:", data.lastEvaluatedKey);
              this.lastEvaluatedKey = data.lastEvaluatedKey;

              // Sort the comments
              audioComments.forEach((comment: AudioComment) => {
                comment.timestamp = Number(comment.timestamp);
              });
              const retrievedComments = audioComments.sort((a, b) => 
                b.timestamp - a.timestamp
              );

              // Update comments
              if (isInitialLoad) {
                this.allComments$.next(retrievedComments);
              } else {
                // Create a Set of existing comment IDs for efficient lookup
                const existingCommentIds = new Set(
                  this.allComments$.getValue().map(comment => comment.commentId)
                );
                
                // Filter out any duplicates from the new comments
                const newUniqueComments = retrievedComments.filter(
                  comment => !existingCommentIds.has(comment.commentId)
                );
                
                // Combine existing and new unique comments
                this.allComments$.next([
                  ...this.allComments$.getValue(),
                  ...newUniqueComments
                ].sort((a, b) => b.timestamp - a.timestamp));
              }
              
              this.isLoading = false;
              // Resolve with hasMore status after everything is complete
              resolve({ hasMore: !!this.lastEvaluatedKey });
            },
            error: async error => {
              console.error('There was an error retrieving this audio comments!', error);
              const toast = await this.toastController.create({
                message: 'There was an error retrieving this mix tapes comments!',
                duration: 1500,
                color: 'warning',
                position: 'top',
              });
              await toast.present();
              this.isLoading = false;
              reject(error);
            }
          });
      });
    }
    catch(error) {
      console.error("An error occurred retrieving the mix tapes comments", error);
      this.isLoading = false;
      throw error;
    }
  }

  async deleteComment(comment: AudioComment){
    console.log("Deleting Comment");
    console.log(JSON.stringify(comment));

    this.http.delete(`${environment.REST_API}/commentAudio?s3Path=${comment.s3Path}&commentId=${comment.commentId}`,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.session.tokens.idToken}`
        }),
        }
    ).subscribe({
      next: async (data) => {
        console.log("Audio comment deleted!");
        console.log(data);

        const trimmedComments = this.allComments$.getValue();
          this.allComments$.next(trimmedComments.filter((c) => {
            return c.commentId !== comment.commentId
          })
        );

        const toast = await this.toastController.create({
          message: 'Comment successfully deleted!',
          duration: 1500,
          color: 'success',
          position: 'top',
        });
        await toast.present();

      },
      error: async error => {
          console.error('There was an error deleting this comment!', error);
          const toast = await this.toastController.create({
            message: 'There was an error deleting the comment!',
            duration: 1500,
            color: 'warning',
            position: 'top',
          });
          await toast.present();
      }
    });
  }
}
